import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Bell, 
  Download, 
  Clock,
  Users,
  Tag,
  Eye,
  Share2,
  ChevronRight,
  MessageSquare,
  ThumbsUp
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { exportAnnouncementToPDF } from '../../../../utils/pdfExport';

const AnnouncementCard = ({ announcement }) => {
  const [isHovered, setIsHovered] = useState(false);

  const handleDownload = () => {
    const doc = exportAnnouncementToPDF(announcement);
    doc.save(`${announcement.title.toLowerCase().replace(/\s+/g, '-')}-announcement.pdf`);
  };

  const getPriorityColor = (priority) => {
    switch (priority.toLowerCase()) {
      case 'high': return 'red';
      case 'medium': return 'yellow';
      case 'low': return 'blue';
      default: return 'gray';
    }
  };

  const color = getPriorityColor(announcement.priority);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className="group relative bg-white rounded-xl shadow-sm hover:shadow-lg transition-all duration-300"
    >
      {/* Priority Banner */}
      <div className={`absolute inset-x-0 top-0 h-2 rounded-t-xl bg-gradient-to-r 
        from-${color}-500 to-${color}-600`}
      />

      <div className="p-6 pt-8">
        {/* Header */}
        <div className="flex items-start gap-4">
          <div className={`p-3 rounded-xl bg-${color}-50`}>
            <Bell className={`w-6 h-6 text-${color}-600`} />
          </div>
          <div className="flex-1">
            <div className="flex justify-between items-start">
              <h3 className="font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
                {announcement.title}
              </h3>
              <span className={`px-2 py-1 text-xs rounded-full 
                ${announcement.priority === 'high' ? 'bg-red-100 text-red-700' :
                  announcement.priority === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                  'bg-blue-100 text-blue-700'}`}
              >
                {announcement.priority} Priority
              </span>
            </div>
            <div className="flex items-center gap-2 mt-2">
              <Tag className="w-4 h-4 text-gray-400" />
              <span className="text-sm text-gray-500">{announcement.department}</span>
            </div>
          </div>
        </div>

        {/* Content Preview */}
        <div className="mt-4">
          <p className="text-sm text-gray-600 line-clamp-2">
            {announcement.content}
          </p>
        </div>

        {/* Engagement Stats */}
        <div className="mt-4 flex items-center justify-between">
          <div className="flex items-center gap-4 text-sm text-gray-500">
            <div className="flex items-center gap-1">
              <Eye className="w-4 h-4" />
              {announcement.views}
            </div>
            <div className="flex items-center gap-1">
              <ThumbsUp className="w-4 h-4" />
              {announcement.acknowledgments}
            </div>
            <div className="flex items-center gap-1">
              <MessageSquare className="w-4 h-4" />
              {announcement.comments}
            </div>
          </div>
          <div className="flex items-center gap-1 text-sm text-gray-500">
            <Clock className="w-4 h-4" />
            {announcement.date}
          </div>
        </div>

        {/* Action Bar */}
        <AnimatePresence>
          {isHovered && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-white via-white to-transparent"
            >
              <div className="flex items-center justify-between">
                <div className="flex gap-2">
                  <button
                    onClick={handleDownload}
                    className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                    title="Download PDF"
                  >
                    <Download className="w-5 h-5" />
                  </button>
                  <button
                    className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                    title="Share Announcement"
                  >
                    <Share2 className="w-5 h-5" />
                  </button>
                </div>
                <Link
                  to={`/sop/announcements/${announcement.id}`}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Eye className="w-4 h-4" />
                  View Details
                  <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

export default AnnouncementCard; 